/**
 * The on-screen text display builder: one card per row, each independently styled (font, size, colour,
 * weight, alignment, position, background, outline, shadow, timing). The parent owns the lines and the
 * live preview sits beside the rows.
 */
import { OUTLINE_STOPS, TEXT_FONTS, TextLine } from '@studio/contracts';
import { Tool, Toolbar } from './toolbar';

/** The ladder notch for a row's effective outline, legacy enum included. */
function outlineIdx(line: TextLine): number {
  const w = line.outlineW ?? (line.outline === 'thick' ? 2 : line.outline === 'thin' ? 0.7 : 0);
  let best = 0;
  for (let i = 1; i < OUTLINE_STOPS.length; i++) if (Math.abs(OUTLINE_STOPS[i]! - w) < Math.abs(OUTLINE_STOPS[best]! - w)) best = i;
  return best;
}

export function TextBuilder({ lines, totalMs, onChange, hideTiming }: {
  lines: TextLine[];
  totalMs: number;
  onChange: (lines: TextLine[]) => void;
  /** Per-clip overlays run for the clip's whole duration; the timing fields would only mislead. */
  hideTiming?: boolean;
}) {
  const setLine = (i: number, patch: Partial<TextLine>) => onChange(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => onChange([...lines, TextLine.parse({ text: '', yPct: Math.min(90, 20 + lines.length * 12) })]);
  const removeLine = (i: number) => onChange(lines.filter((_, idx) => idx !== i));

  return (
    <div className="text-builder">
      {lines.map((line, i) => (
        <div key={i} className="card text-line">
          <div className="row text-line-top">
            <input className="text-line-input" value={line.text} placeholder={`Row ${i + 1} text…`} aria-label={`Row ${i + 1} text`}
              onChange={(e) => setLine(i, { text: e.target.value })} />
            <Toolbar dense>
              <Tool icon="trash" title="Remove this row" danger onClick={() => removeLine(i)} />
            </Toolbar>
          </div>
          <div className="row text-line-controls">
            <label className="tool-field">
              <span className="tool-label">Font</span>
              <select value={line.font} aria-label="Font" onChange={(e) => setLine(i, { font: e.target.value })}>
                {TEXT_FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
              </select>
            </label>
            <label className="tool-field narrow">
              <span className="tool-label">Size %</span>
              <input type="number" min={1} max={25} step={0.5} value={line.sizePct} aria-label="Size percent"
                onChange={(e) => setLine(i, { sizePct: Number(e.target.value) || 5 })} />
            </label>
            <label className="tool-field narrow">
              <span className="tool-label">Colour</span>
              <input type="color" value={line.colour} aria-label="Text colour" onChange={(e) => setLine(i, { colour: e.target.value })} />
            </label>
            <div className="seg text-seg">
              <button type="button" className={line.bold ? 'on' : ''} title="Bold" onClick={() => setLine(i, { bold: !line.bold })}>B</button>
              <button type="button" className={line.italic ? 'on' : ''} title="Italic" onClick={() => setLine(i, { italic: !line.italic })}><i>I</i></button>
            </div>
            <div className="seg text-seg">
              {(['left', 'center', 'right'] as const).map((a) => (
                <button key={a} type="button" className={line.align === a ? 'on' : ''} title={`Align ${a}`} onClick={() => setLine(i, { align: a })}>
                  {a === 'left' ? '⟸' : a === 'right' ? '⟹' : '≡'}
                </button>
              ))}
            </div>
            <label className="tool-field">
              <span className="tool-label">Position {Math.round(line.yPct)}%</span>
              <input type="range" min={2} max={98} value={line.yPct} aria-label="Vertical position" className="gain text-y"
                onChange={(e) => setLine(i, { yPct: Number(e.target.value) })} />
            </label>
          </div>
          <div className="row text-line-controls">
            <label className="tool-field">
              <span className="tool-label">Background</span>
              <select value={line.background} aria-label="Background" onChange={(e) => setLine(i, { background: e.target.value as TextLine['background'] })}>
                <option value="none">None</option>
                <option value="box">Box behind text</option>
                <option value="band">Full-width band</option>
              </select>
            </label>
            {line.background !== 'none' && (
              <>
                <label className="tool-field narrow">
                  <span className="tool-label">Fill</span>
                  <input type="color" value={line.backgroundColour} aria-label="Background colour" onChange={(e) => setLine(i, { backgroundColour: e.target.value })} />
                </label>
                <label className="tool-field">
                  <span className="tool-label">Opacity {Math.round(line.backgroundOpacity * 100)}%</span>
                  <input type="range" min={0} max={100} value={line.backgroundOpacity * 100} aria-label="Background opacity" className="gain text-y"
                    onChange={(e) => setLine(i, { backgroundOpacity: Number(e.target.value) / 100 })} />
                </label>
              </>
            )}
            <label className="tool-field">
              <span className="tool-label">Outline {(line.outlineW ?? (line.outline === 'thick' ? 2 : line.outline === 'thin' ? 0.7 : 0)).toFixed(1)}</span>
              <input type="range" min={0} max={OUTLINE_STOPS.length - 1} step={1} className="gain text-y"
                value={outlineIdx(line)} aria-label="Outline width"
                onChange={(e) => setLine(i, { outlineW: OUTLINE_STOPS[Number(e.target.value)]! })} />
            </label>
            <label className="tool-field narrow">
              <span className="tool-label">Outline colour</span>
              <input type="color" value={line.outlineColour} aria-label="Outline colour" onChange={(e) => setLine(i, { outlineColour: e.target.value })} />
            </label>
            <button type="button" className={`chip${line.shadow ? ' acc' : ''}`} onClick={() => setLine(i, { shadow: !line.shadow })}>Shadow</button>
            {!hideTiming && (
              <>
                <label className="tool-field narrow">
                  <span className="tool-label">From s</span>
                  <input type="number" min={0} step={0.1} value={line.fromMs !== null ? line.fromMs / 1000 : ''} placeholder="start" aria-label="Show from seconds"
                    onChange={(e) => setLine(i, { fromMs: e.target.value ? Math.round(Number(e.target.value) * 1000) : null })} />
                </label>
                <label className="tool-field narrow">
                  <span className="tool-label">To s</span>
                  <input type="number" min={0} step={0.1} value={line.toMs !== null ? line.toMs / 1000 : ''} placeholder="end" aria-label="Show until seconds"
                    onChange={(e) => setLine(i, { toMs: e.target.value ? Math.round(Number(e.target.value) * 1000) : null })} />
                </label>
              </>
            )}
          </div>
        </div>
      ))}
      <div className="row">
        <button type="button" className="btn" onClick={addLine} disabled={lines.length >= 12}>Add a row</button>
        <span className="sub text-builder-note">
          {hideTiming
            ? `Each row is styled on its own · shown for this clip's whole ${(totalMs / 1000).toFixed(1)} s, however it is trimmed`
            : `Each row is styled on its own · blank timing shows for the whole cut (${(totalMs / 1000).toFixed(1)} s)`}
        </span>
      </div>
    </div>
  );
}
